import { createSlice } from '@reduxjs/toolkit';
import history from 'routing/history';
import { decodeJWT, clearJWT, createJWT, pushAccessTokenToJWT } from 'auth';
import { fireLoginTrackingEvent } from 'analytics';
import { post, get } from 'api/fetch';
import Geo from 'api/geo';
import { fetchPlanholderInfo as fetchNIPlanholderInfo } from 'products/nomad_insurance/state/planholder';
import { fetchAmbassador } from 'products/global/views/Ambassador/state/ambassador';
// ================== Reducers ================== //

const slice = createSlice({
  name: 'app',
  initialState: {
    error: null,
    isInitialized: false,
    isInitInProgress: false,
    initializedProduct: null,

    user: {
      isLoading: false,
      loadingField: false,
      error: null,
      isAuthenticated: false,
      accessToken: null,
      userID: null,
      citizenshipCountry: '',
      dateOfBirth: '',
      email: '',
      firstName: '',
      lastName: '',
      gender: '',
      consentToTermsAndConditions: '',
      consentToMarketing: ''
    },

    facebookLogin: {
      isLoading: false,
      error: false,
      user: null
    },

    geo: {
      isLoading: false,
      error: null,
      countryCode: null
    },
    profile: {
      isLoading: false,
      error: null,
      data: null
    }
  },
  reducers: {
    // initialize
    initializeRequest: (state) => {
      state.isInitInProgress = true;
    },
    initializeSuccess: (state, { payload }) => {
      state.isInitInProgress = false;
      state.isInitialized = true;
      state.initializedProduct = payload.initializedProduct || state.initializedProduct;
      state.user = {
        ...state.user,
        isAuthenticated: true,
        ...payload.user
      };
    },
    initializeFailure: (state, { payload }) => {
      state.isInitInProgress = false;
      state.isInitialized = true;
      state.user = {
        isAuthenticated: false,
        role: null
      };
      state.error = payload;
    },
    updateAuthTokenRequest: (state) => {
      state.user.isLoading = true;
    },
    updateAuthTokenSuccess: (state) => {
      state.user.isLoading = false;
    },
    updateAuthTokenFailure: (state, { payload }) => {
      state.user.isLoading = false;
      state.error = payload;
    },
    updateAppInfo: (state, { payload }) => {
      state.user = { ...state.user, ...payload.user };
      state.isInitialized = payload.isInitialized || state.isInitialized;
      state.initializedProduct = payload.initializedProduct || state.initializedProduct;
    },
    // logout
    logOutUser: (state) => {
      state.isInitInProgress = false;
      state.isInitialized = true;
      state.user = {
        isAuthenticated: false,
        role: null
      };
    },
    // facebookLogin
    facebookLoginRequest: (state) => {
      state.facebookLogin.isLoading = true;
      state.facebookLogin.error = null;
    },
    facebookLoginSuccess: (state, { payload }) => {
      state.facebookLogin.isLoading = false;
      state.facebookLogin.error = null;
      state.facebookLogin.user = payload;
    },
    facebookLoginFailure: (state, { payload }) => {
      state.facebookLogin.isLoading = false;
      state.facebookLogin.error = payload;
      state.facebookLogin.user = null;
    },
    setUserIsAmbassador: (state, { payload }) => {
      state.user.isAmbassador = payload;
    },
    // fetchGeoInfo
    fetchGeoInfoRequest: (state) => {
      state.geo.isLoading = true;
      state.geo.error = null;
    },
    fetchGeoInfoSuccess: (state, { payload }) => {
      state.geo.isLoading = false;
      state.geo.error = null;
      state.geo.countryCode = payload.country_code;
    },
    fetchGeoInfoFailure: (state, { payload }) => {
      state.geo.isLoading = false;
      state.geo.error = payload;
      state.geo.countryCode = null;
    },
    // fetchUserProfile
    fetchUserProfileRequest: (state) => {
      state.profile.isLoading = true;
      state.profile.error = null;
    },
    fetchUserProfileSuccess: (state, { payload }) => {
      state.profile.isLoading = false;
      state.profile.error = null;
      state.profile.data = payload;
    },
    fetchUserProfileFailure: (state, { payload }) => {
      state.profile.isLoading = false;
      state.profile.error = payload;
      state.profile.data = null;
    }
  }
});

export default slice.reducer;

const { actions } = slice;

// ================== Util ================== //

export const tokenExists = () => {
  const userID = decodeJWT('userID');
  const accessToken = decodeJWT('accessToken');
  return !!userID && !!accessToken;
};

const fetchFullProfile = async () => {
  const response = await get('/v1/api/me/personal-details', { isUserService: true });
  const user = response.user ?? {};
  const profile = response.profile ?? {};
  const agreements = response.agreedTerms ?? [];
  const hasAgreedToTerms = agreements.some((a) => a.termType === 'TERMS_OF_SERVICE');
  const hasAgreedToMarketing = agreements.some((a) => a.termType === 'MARKETING');

  return {
    email: user.email,
    isAmbassador: user.isAmbassador,
    firstName: profile.firstName,
    lastName: profile.lastName,
    dateOfBirth: profile.dateOfBirth,
    gender: profile.gender,
    citizenship: profile.citizenship,
    citizenshipCountry: profile.citizenship,
    homeCountry: profile.homeCountry,
    beneficiaryFullName: profile.beneficiaryFullName,
    hasCompletedGlobalProfile: !!(
      profile.firstName &&
      profile.lastName &&
      user.email &&
      profile.dateOfBirth &&
      profile.gender &&
      profile.citizenship &&
      hasAgreedToTerms &&
      hasAgreedToMarketing
    )
  };
};

export const isUserAuthenticated = (user) => (dispatch) => {
  const { id } = user;
  const userID = decodeJWT('userID');

  if (userID !== id) {
    clearJWT();
    dispatch(actions.initializeFailure());
  }
};

// ================== Actions ================== //

export const initializeUser = () => async (dispatch) => {
  try {
    if (tokenExists()) {
      dispatch(actions.initializeRequest());
      const accessToken = decodeJWT('accessToken');
      const userID = decodeJWT('userID');

      const response = await fetchFullProfile();

      dispatch(
        actions.initializeSuccess({
          user: {
            userID,
            accessToken,
            ...response
          },
          initializedProduct: 'global'
        })
      );
    } else {
      dispatch(actions.initializeFailure());
    }
  } catch (error) {
    dispatch(actions.initializeFailure(error));
  }
};

const TOKEN_REFRESH_DELAY = 1000 * 60 * 60 * 24; // 24h
export const updateAuthToken = () => async (dispatch) => {
  try {
    if (!tokenExists()) return;

    const refreshedAt = decodeJWT('updatedAt');
    if (Date.now() > refreshedAt + TOKEN_REFRESH_DELAY) {
      dispatch(actions.updateAuthTokenRequest());

      const response = await post('/v1/api/auth/token/refresh', null, {
        isUserService: true
      });
      await pushAccessTokenToJWT(response);

      dispatch(actions.updateAuthTokenSuccess());
    }
  } catch (error) {
    dispatch(actions.updateAuthTokenFailure(error));
  }
};

export const fetchGeoInfo = () => async (dispatch) => {
  dispatch(actions.fetchGeoInfoRequest());
  try {
    const { data } = await Geo.reveal();
    dispatch(actions.fetchGeoInfoSuccess(data));
  } catch (error) {
    dispatch(actions.fetchGeoInfoFailure(error));
  }
};

export const initialize =
  (redirectOnUnauthorized = true, product = 'nomad_insurance') =>
  async (dispatch, getState) => {
    try {
      if (tokenExists()) {
        const accessToken = decodeJWT('accessToken');

        dispatch(actions.initializeRequest());

        if (product === 'nomad_insurance') {
          await dispatch(fetchNIPlanholderInfo(redirectOnUnauthorized));
          const { planholder } = getState().nomadInsurance.planholder;

          dispatch(
            actions.initializeSuccess({
              user: {
                userID: planholder.userID,
                accessToken
              },
              initializedProduct: product
            })
          );
        }

        if (product === 'global') {
          await dispatch(initializeUser());
        }

        if (product === 'ambassador') {
          await dispatch(fetchAmbassador(redirectOnUnauthorized));
          const { ambassadorUserDetails } = getState().global.ambassador.ambassadorUser;

          dispatch(
            actions.initializeSuccess({
              user: {
                userID: ambassadorUserDetails.userID,
                email: ambassadorUserDetails?.email,
                accessToken
              },
              initializedProduct: product
            })
          );
        }

        if (!('hasCompletedGlobalProfile' in getState().app.user)) {
          const response = await fetchFullProfile();
          dispatch(
            actions.initializeSuccess({
              user: {
                ...response
              }
            })
          );
        }
      } else {
        dispatch(actions.initializeFailure());
      }
    } catch (error) {
      clearJWT();
      dispatch(actions.initializeFailure(error));
    }

    // Fetch geo-related information
    // Used to e.g. display the Remote Doctor page in a specific country
    // or display more user-location-focused UI
    dispatch(fetchGeoInfo());
  };

export const updateAppInfo = (info) => (dispatch) => {
  dispatch(actions.updateAppInfo(info));
};

export const logOutUser = (redirect) => (dispatch) => {
  clearJWT();

  window.Intercom('shutdown');
  window.Intercom('boot', {
    app_id: process.env.REACT_APP_INTERCOM_APP_ID
  });

  dispatch({ type: 'RESET_APP' });
  dispatch(actions.logOutUser());

  if (redirect !== 'none') {
    history.push(redirect || '/');
  }
};

export const facebookLogin = (facebookResponse) => async (dispatch) => {
  if (!facebookResponse.userID) {
    return;
  }

  dispatch(actions.facebookLoginRequest());

  const body = {
    facebookID: facebookResponse.userID,
    accessToken: facebookResponse.accessToken,
    profilePictureUrl: facebookResponse.picture.data.url,
    firstName: facebookResponse.name ? facebookResponse.name.split(' ')[0] : null,
    lastName: facebookResponse.name ? facebookResponse.name.split(' ')[1] : null,
    email: facebookResponse.email ? facebookResponse.email : null
  };

  try {
    const response = await post(
      '/v1/api/auth/sign-in/facebook',
      { token: facebookResponse.accessToken },
      { isUserService: true }
    );
    await createJWT({
      userID: response.userId,
      accessToken: response.token
    });
    dispatch(actions.facebookLoginSuccess(body));
    fireLoginTrackingEvent(response.userId, 'facebook');
  } catch (error) {
    dispatch(actions.facebookLoginFailure(error));
  }
};

export const fetchUserProfile = () => async (dispatch) => {
  dispatch(actions.fetchUserProfileRequest());

  try {
    const response = await get('/users/v1', { isNIOrchestrator: true });
    dispatch(actions.fetchUserProfileSuccess(response));
  } catch (error) {
    dispatch(actions.fetchUserProfileFailure(error));
  }
};

export const { setUserIsAmbassador } = slice.actions;
